/**
 * Identifier generation, owned by the SDK rather than asked of the game.
 *
 * Two different identifier formats appear in the contracts, and they are not
 * interchangeable:
 *
 *   `ackId`              ULID — `^[0-9A-HJKMNP-TV-Z]{26}$` (action-ack.schema.json)
 *   `clientSubmissionId` UUID — `format: uuid`             (score-submission.schema.json)
 *
 * Earlier revisions of this package took a single `newId()` from the game and
 * used it for both. No such function can exist: a value matching the ULID
 * pattern is never a valid UUID and the reverse holds too, so one of the two
 * schemas rejected every submission. Whichever generator a game supplied, it was
 * wrong somewhere — and the failure surfaced far from its cause, as a schema
 * error on an ACK the game never saw.
 *
 * The SDK therefore generates both itself and exposes neither as a required
 * option. `crypto.getRandomValues` is available in every browser the platform
 * supports and in Node 18+, so there is no dependency to add.
 */
/** Crockford base32, the ULID alphabet: no I, L, O, or U. */
const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const ULID_TIME_LENGTH = 10;
const ULID_RANDOM_LENGTH = 16;
function randomBytes(count) {
    const buffer = new Uint8Array(count);
    // Typed as always-present by the DOM lib, but this package also runs in Node
    // and in test doubles, so the check is a runtime one rather than a type one.
    const source = globalThis.crypto;
    if (typeof source?.getRandomValues !== 'function') {
        throw new Error('TRAIDENT SDK requires crypto.getRandomValues, which this environment does not provide');
    }
    source.getRandomValues(buffer);
    return buffer;
}
/** Indexing a string returns `string | undefined`; `charAt` never does. */
function symbol(value) {
    return CROCKFORD.charAt(value);
}
function toHex(byte) {
    return byte.toString(16).padStart(2, '0');
}
/**
 * A ULID: 48 bits of millisecond timestamp then 80 bits of randomness.
 *
 * The timestamp half is encoded most-significant-first so that lexical order
 * matches chronological order, which is the property the format exists for.
 */
export function newUlid(now = Date.now()) {
    let timePart = '';
    let remaining = Math.floor(now);
    for (let index = 0; index < ULID_TIME_LENGTH; index += 1) {
        timePart = symbol(remaining % 32) + timePart;
        remaining = Math.floor(remaining / 32);
    }
    let randomPart = '';
    for (const byte of randomBytes(ULID_RANDOM_LENGTH)) {
        // Masking to 5 bits keeps the mapping uniform over the 32-character
        // alphabet; a modulo of a byte would bias the first 8 symbols.
        randomPart += symbol(byte & 0x1f);
    }
    return timePart + randomPart;
}
/** A RFC 4122 version 4 UUID. */
export function newUuid() {
    const source = globalThis.crypto;
    if (typeof source?.randomUUID === 'function') {
        return source.randomUUID();
    }
    const bytes = randomBytes(16);
    // Version 4 in the high nibble of byte 6, variant 10xx in byte 8, per RFC
    // 4122 section 4.4. Reads go through `at()` so the element type is not
    // `number | undefined` at the call site.
    bytes[6] = ((bytes.at(6) ?? 0) & 0x0f) | 0x40;
    bytes[8] = ((bytes.at(8) ?? 0) & 0x3f) | 0x80;
    const hex = Array.from(bytes, toHex);
    return [
        hex.slice(0, 4).join(''),
        hex.slice(4, 6).join(''),
        hex.slice(6, 8).join(''),
        hex.slice(8, 10).join(''),
        hex.slice(10, 16).join(''),
    ].join('-');
}
/**
 * A 64-character lowercase hex digest, the shape `proof.stateDigest` requires.
 *
 * A game that maintains its own deterministic state serialiser should hash that
 * and pass the result. This exists so that a game which does not yet have one
 * still sends a well-formed, submission-specific value rather than a hardcoded
 * string of `f`s — and so that the SDK never silently invents a digest that
 * claims more integrity than it has. It is a digest of the submission itself,
 * which is exactly what it looks like and no more.
 */
export async function digestOf(input) {
    const subtle = globalThis.crypto?.subtle;
    if (subtle === undefined) {
        throw new Error('TRAIDENT SDK requires crypto.subtle to compute a state digest; ' +
            'pass stateDigest explicitly in an environment without it');
    }
    const encoded = new TextEncoder().encode(input);
    const hashed = await subtle.digest('SHA-256', encoded);
    return Array.from(new Uint8Array(hashed), toHex).join('');
}
